import type { EEGTileTransportMode } from "../services/EEGSegmentCache";

const TILE_RANGE_ALIGNMENT_MIN_MS = 1000;
const HIGH_SPEED_PLAY_ALIGNMENT_THRESHOLD = 100;
const HIGH_SPEED_PLAY_OVERLAP_MS = 30000;

function getRangeAlignmentStepMs(
  requestedWindowMs: number,
  speed: number,
  transportMode: EEGTileTransportMode,
): number {
  if (
    transportMode === "play" &&
    speed >= HIGH_SPEED_PLAY_ALIGNMENT_THRESHOLD
  ) {
    return Math.max(
      TILE_RANGE_ALIGNMENT_MIN_MS,
      Math.min(
        requestedWindowMs,
        Math.round(
          Math.max(
            TILE_RANGE_ALIGNMENT_MIN_MS,
            requestedWindowMs - HIGH_SPEED_PLAY_OVERLAP_MS,
          ) / 1000,
        ) * 1000,
      ),
    );
  }

  // Mirror systemapi EegTileService.getRangeAlignmentStepMs() for clinical/
  // normal-speed windows so the viewer expects the same effective window start
  // that the backend returns in X-Tile-Range.
  return Math.max(
    TILE_RANGE_ALIGNMENT_MIN_MS,
    Math.round(requestedWindowMs / 4 / 1000) * 1000,
  );
}

export interface BackendAlignedTileWindow {
  requestedStartMs: number;
  requestedEndMs: number;
  requestedWindowMs: number;
  alignmentStepMs: number;
  startMs: number;
  endMs: number;
  startSec: number;
  endSec: number;
}

interface AlignTileWindowOptions {
  windowStartSec: number;
  bufferSizeSec: number;
  speed: number;
  transportMode: EEGTileTransportMode;
  recordingDurationSec?: number;
}

export function alignTileWindowToBackend(
  options: AlignTileWindowOptions,
): BackendAlignedTileWindow {
  const requestedWindowMs = Math.max(
    1,
    Math.round(Math.max(0.001, options.bufferSizeSec) * 1000),
  );
  const recordingDurationMs =
    typeof options.recordingDurationSec === "number" &&
    Number.isFinite(options.recordingDurationSec) &&
    options.recordingDurationSec > 0
      ? Math.max(1, Math.round(options.recordingDurationSec * 1000))
      : null;
  const requestedStartMs = Math.max(
    0,
    Math.floor(Math.max(0, options.windowStartSec) * 1000),
  );
  const boundedRequestedStartMs =
    recordingDurationMs === null
      ? requestedStartMs
      : Math.max(
          0,
          Math.min(requestedStartMs, Math.max(0, recordingDurationMs - 1)),
        );
  const boundedRequestedEndMs =
    recordingDurationMs === null
      ? boundedRequestedStartMs + requestedWindowMs
      : Math.max(
          boundedRequestedStartMs + 1,
          Math.min(
            recordingDurationMs,
            boundedRequestedStartMs + requestedWindowMs,
          ),
        );

  const alignmentStepMs = getRangeAlignmentStepMs(
    requestedWindowMs,
    options.speed,
    options.transportMode,
  );

  let alignedStartMs = boundedRequestedStartMs;
  let alignedEndMs = boundedRequestedEndMs;

  if (
    recordingDurationMs !== null &&
    boundedRequestedEndMs === recordingDurationMs
  ) {
    alignedStartMs = Math.max(0, boundedRequestedEndMs - requestedWindowMs);
    alignedEndMs = boundedRequestedEndMs;
  } else {
    const snappedStartMs =
      Math.floor(boundedRequestedStartMs / alignmentStepMs) * alignmentStepMs;
    alignedStartMs =
      recordingDurationMs === null
        ? Math.max(0, snappedStartMs)
        : Math.max(
            0,
            Math.min(snappedStartMs, Math.max(0, recordingDurationMs - 1)),
          );
    alignedEndMs =
      recordingDurationMs === null
        ? alignedStartMs + requestedWindowMs
        : Math.max(
            alignedStartMs + 1,
            Math.min(recordingDurationMs, alignedStartMs + requestedWindowMs),
          );
  }

  return {
    requestedStartMs: boundedRequestedStartMs,
    requestedEndMs: boundedRequestedEndMs,
    requestedWindowMs,
    alignmentStepMs,
    startMs: alignedStartMs,
    endMs: alignedEndMs,
    startSec: alignedStartMs / 1000,
    endSec: alignedEndMs / 1000,
  };
}
